import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { AssessmentResult, AssessmentSettings, ExperienceType } from '@shared/models';
import { EChartsOption } from 'echarts';
import { AssessmentService } from '@shared/services/assessment.service';
import { getAssessmentDisplayParams, setupAssessmentChart } from '@shared/utils/assessment.utils';
import { UIState } from '@shared/directives/ui-state/ui-state.directive';
import { LinkService } from '@shared/services/link.service';
import { environment } from '@env/environment';

@Component({
  selector: 'assessment-report',
  templateUrl: './assessment-report.component.html',
  styleUrls: ['./assessment-report.component.scss']
})
export class AssessmentReportComponent implements OnInit, OnChanges {
  @Input() fromEditorContext: boolean = false;
  @Input() dialogMode: boolean;
  @Input() learnerReportMode: boolean;
  @Input() ltiReturnEnabled: boolean = false;
  @Input() showScoreMedian: boolean;
  @Input() canEdit: boolean;
  @Input() showBackToSimulation: boolean;
  @Input() isLastSimulation: boolean;
  @Input() hasClickedAssessmentButton: boolean;
  @Input() courseId: string;
  @Input() courseTitle: string;
  @Input() experienceType: ExperienceType;
  @Input() assessmentSettings?: AssessmentSettings;
  @Input() assessmentResult?: AssessmentResult;
  @Input() errorMessage?: string;
  @Input() externalLink?: any;
  @Output() onShowSessionLogs = new EventEmitter();
  @Output() onRestart = new EventEmitter();
  @Output() onClose = new EventEmitter();
  @Output() onOpenAssessmentDashboard = new EventEmitter<boolean>();
  @Output() onOpenProgramIndex = new EventEmitter();
  @Output() onReturnToLms = new EventEmitter();

  public downloadInProgress: boolean = false;
  public displayParams: any;
  public chartOption: EChartsOption;
  public state: UIState;

  get hasExternalLink(): boolean {
    return this.externalLink && this.externalLink.enabled && this.externalLink.type && this.externalLink.target;
  }

  constructor(
    private _assessmentService: AssessmentService,
    private _linkService: LinkService
  ) {}

  ngOnInit(): void {
    this.setupWithAssessmentResult();
  }
  
  ngOnChanges() {
    this.setupWithAssessmentResult();
  }

  private setupWithAssessmentResult() {
    if (this.assessmentResult) {
      this.state = UIState.default;
      this.displayParams = getAssessmentDisplayParams(this.assessmentResult, this.assessmentSettings);
      this.chartOption = setupAssessmentChart(this.assessmentResult, this.displayParams.scoreMax, false);
    } else if (this.errorMessage) {
      this.state = UIState.error;
    } else {
      this.state = UIState.inprogress;
    }
  }

  isSimulation() {
    return this.experienceType === ExperienceType.ai;
  }

  downloadReport() {
    this.downloadInProgress = true;
    this._assessmentService
      .getAssessmentExportDataBySessionId(this.courseId, this.assessmentResult.session_id)
      .subscribe(success => {
        this.downloadInProgress = false;
      });
  }

  showSessionLogs() {
    this.onShowSessionLogs.emit();
  }

  openAssessmentEditor() {
    this.onOpenAssessmentDashboard.emit(true);
  }
  
  openAssessmentDashboard() {
    this.onOpenAssessmentDashboard.emit(false);
  }

  closeDialog() {
    this.onClose.emit();
  }

  restart() {
    this.onRestart.emit();
  }

  openProgramIndex() {
    this.onOpenProgramIndex.emit();
  }

  returnToLms() {
    this.onReturnToLms.emit();
  }

  continueOrGoToProgramIndex() {
    if (this.showBackToSimulation || this.isLastSimulation) {
      this.openProgramIndex();
    } else {
      this.continueToNextSimulation();
    }
  }

  continueToNextSimulation() {
    this.openURL(`${environment.coursesApiUrl}/api/courses/${this.courseId}/simulation/next`);
  }
  private openURL(url: string) {
    (window.top || window).location.assign(url);
  }

  openHelp() {
    window.open('https://help.spaces.wondavr.com/en/articles/9728097-ai-assessment');
  }

  applyExternalLink() {
    if (!this.hasExternalLink) {
      return;
    }

    let target = this.externalLink.target;
    let newTab = this.externalLink.newTab;

    switch (this.externalLink?.type) {
      case "url":
        window.open(target, newTab ? "_blank" : "_top");
        break;
      case "experience":
        this._linkService.launchCourseById(target, newTab, this.experienceType);
        break;
    }
  }
}
